import { escapeHTML } from '../../utils/escape.js';
import { ReservationGroup } from './ReservationGroup.js';
import { ReservationCard, ReservationRow } from './ReservationRow.js';
import { RESERVATION_COLUMNS, RESERVATION_TH } from './reservationColumns.js';

/**
 * Contenedor de la vista de reservas del panel de staff (Fase 1, slice 2).
 * Función de render pura: devuelve un string HTML, sin estado ni efectos.
 * `js/admin.js` (wiring) le pasa el modelo de vista devuelto por
 * `admin-reservations.service.js` junto con un ui-state que decide qué slot
 * renderizar (loading / error / lista) y en qué MODO.
 *
 * Modo (Req: Archived Reservations View):
 * - `activas` (default): la lista principal AGRUPADA por día
 *   (`{ groups, isEmpty }` — delega cada día a `ReservationGroup`), con
 *   date-picker + "Nueva reserva" en el header.
 * - `archivadas`: una lista PLANA (no agrupada) de reservas archivadas
 *   (`{ rows }`), de solo-lectura. El date-picker y "Nueva reserva" se ocultan.
 * El header siempre trae un botón toggle (`reserva-toggle-archivadas`) que
 * alterna entre ambos modos.
 *
 * Req: Loading, Empty, and Error States — loading/empty/error son estados
 * mutuamente excluyentes y visualmente distintos; el error trae una acción
 * de reintento (`data-action="reserva-retry"`).
 * Req: Default Window and Date Picker — el picker de fecha único filtra
 * `fecha >= valor elegido` (mismo criterio que el default "hoy"); el
 * wiring lee el cambio vía `data-action="reserva-change-window"`.
 */

const EMPTY_MESSAGE = 'No hay reservas próximas.';
const EMPTY_ARCHIVED_MESSAGE = 'No hay reservas archivadas.';
const ERROR_MESSAGE = 'No pudimos cargar las reservas. Probá de nuevo.';


/**
 * Lista plana de archivadas: una sola tabla (no agrupada por día) con las
 * filas de solo-lectura, ya ordenadas por fecha DESC desde el servicio.
 * @param {Array<object>} rows
 * @returns {string}
 */
function renderArchivedList(rows) {
    const headerCells = RESERVATION_COLUMNS.map(({ label }) => {
        const align = label === 'Personas' ? 'text-center' : label === 'Acciones' ? 'text-right' : 'text-left';
        return `<th scope="col" class="${RESERVATION_TH} ${align}">${label}</th>`;
    }).join('');
    const colGroup = RESERVATION_COLUMNS.map(({ width }) => `<col style="width:${width}%" />`).join('');

    return `
        <!-- Desktop (lg+): tabla plana. En mobile se oculta (7 columnas no entran). -->
        <div class="hidden lg:block bg-surface border border-border-subtle rounded-2xl overflow-hidden">
            <table class="w-full table-fixed text-left border-collapse">
                <colgroup>${colGroup}</colgroup>
                <thead class="border-b border-border-subtle">
                    <tr>${headerCells}</tr>
                </thead>
                <tbody>
                    ${rows.map((row) => ReservationRow(row, { readOnly: true })).join('')}
                </tbody>
            </table>
        </div>
        <!-- Mobile (<lg): tarjetas read-only apiladas. -->
        <div class="lg:hidden space-y-3">
            ${rows.map((row) => ReservationCard(row, { readOnly: true })).join('')}
        </div>
    `;
}

/**
 * @param {object|null} viewModel — forma según `uiState.mode`:
 *   activas → `{ groups, isEmpty }`; archivadas → `{ rows }`. Solo se usa
 *   cuando `uiState.status === 'ready'`.
 * @param {{ status: 'loading'|'error'|'ready', mode?: 'activas'|'archivadas' }} uiState
 * @returns {string}
 */
function renderBody(viewModel, uiState) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando reservas...</p>
            </div>
        `;
    }

    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${ERROR_MESSAGE}</p>
                <button data-action="reserva-retry"
                    class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">
                    Reintentar
                </button>
            </div>
        `;
    }

    if (uiState.mode === 'archivadas') {
        const rows = viewModel?.rows ?? [];
        if (rows.length === 0) {
            return `
                <div class="flex flex-col items-center justify-center py-16 text-center">
                    <p class="text-sm text-text-muted font-medium">${EMPTY_ARCHIVED_MESSAGE}</p>
                </div>
            `;
        }
        return renderArchivedList(rows);
    }

    if (!viewModel || viewModel.isEmpty) {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center">
                <p class="text-sm text-text-muted font-medium">${EMPTY_MESSAGE}</p>
            </div>
        `;
    }

    return `<div>${viewModel.groups.map(ReservationGroup).join('')}</div>`;
}

/**
 * @param {object|null} viewModel
 * @param {{ status: 'loading'|'error'|'ready', from?: string, mode?: 'activas'|'archivadas' }} [uiState]
 * @returns {string} HTML de la vista completa (header + picker + toggle + slot).
 */
export const ReservationsView = (viewModel, uiState = {}) => {
    const from = uiState.from ?? '';
    const mode = uiState.mode ?? 'activas';
    const isArchived = mode === 'archivadas';
    const toggleLabel = isArchived ? 'Ver activas' : 'Ver archivadas';

    // En archivadas ocultamos el date-picker y "Nueva reserva" (lista de
    // solo-lectura); el toggle está siempre presente.
    const activasControls = isArchived
        ? ''
        : `
                    <div>
                        <label for="reserva-window-from" class="sr-only">Desde</label>
                        <input id="reserva-window-from" type="date" data-action="reserva-change-window" value="${escapeHTML(from)}"
                            class="bg-surface border border-border-subtle rounded-xl px-4 py-2 text-xs font-bold text-text-primary focus:outline-none focus:border-brand">
                    </div>
                    <button type="button" data-action="reserva-nueva-abrir"
                        class="inline-flex items-center gap-2 px-5 py-2 bg-brand text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition-transform">
                        <i class="fa-solid fa-plus" aria-hidden="true"></i>
                        Nueva reserva
                    </button>`;

    return `
        <div>
            <div class="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div class="flex items-baseline gap-3">
                    <h2 class="text-lg font-black uppercase tracking-tight text-text-primary">Reservas</h2>
                    <span class="text-xs text-text-muted">${isArchived ? 'archivadas' : 'desde'}</span>
                </div>
                <div class="flex items-center gap-3">
                    ${activasControls}
                    <button type="button" data-action="reserva-toggle-archivadas"
                        class="inline-flex items-center gap-2 px-5 py-2 border border-border-subtle text-text-muted rounded-xl text-xs font-black uppercase tracking-widest hover:text-brand transition-colors">
                        <i class="fa-solid fa-box-archive" aria-hidden="true"></i>
                        ${toggleLabel}
                    </button>
                </div>
            </div>
            ${renderBody(viewModel, uiState)}
        </div>
    `;
};
